import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import {
  X, CircleNotch, WarningCircle, CheckCircle, ArrowClockwise,
  Hash, Copy, Check, FilePdf, Books, ListBullets, CaretDown, CaretRight,
} from "@phosphor-icons/react";

/*
 * KnowledgeV2DocDetail — modal Apple-Premium com detalhes profundos:
 *   • pipeline states com progresso real
 *   • contadores (total, concluídas, OCR, vazias, erro, blocos)
 *   • lista expansível de páginas com falha + retry (usa mesmo endpoint
 *     de retry porque o backend reseta failed→pending)
 *   • hash SHA-256 visível apenas para admins
 *   • polling contido dentro do modal enquanto o doc está em progresso
 *
 * Não expõe: storage_path, credenciais, dados do Qdrant.
 */

const STATE_LABEL = {
  uploaded: "Enviado",
  queued: "Aguardando processamento",
  extracting: "Extraindo texto",
  running_ocr: "Executando OCR",
  chunking: "Criando blocos",
  generating_embeddings: "Gerando índice semântico",
  indexing: "Indexando",
  ready: "Pronto",
  partially_processed: "Concluído parcialmente",
  failed: "Falha",
};
const TERMINAL_STATES = new Set(["ready", "partially_processed", "failed"]);

const PAGE_STATUS_LABEL = {
  pending:   "Aguardando",
  extracted: "Texto extraído",
  ocr_ran:   "OCR aplicado",
  empty:     "Página vazia",
  failed:    "Falha",
};

// Pipeline steps em ordem canônica
const PIPELINE = [
  { key: "queued",                label: "Fila" },
  { key: "extracting",            label: "Extração de texto" },
  { key: "running_ocr",           label: "OCR" },
  { key: "chunking",              label: "Blocos" },
  { key: "generating_embeddings", label: "Embeddings" },
  { key: "indexing",              label: "Indexação" },
  { key: "ready",                 label: "Pronto" },
];

function formatDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}
function formatBytes(n) {
  if (!n && n !== 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
function durationText(a, b) {
  if (!a || !b) return "—";
  try {
    const ms = new Date(b).getTime() - new Date(a).getTime();
    if (ms <= 0) return "—";
    if (ms < 60_000) return `${Math.round(ms / 1000)}s`;
    if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}min ${Math.round((ms % 60_000) / 1000)}s`;
    const h = Math.floor(ms / 3_600_000);
    const m = Math.floor((ms % 3_600_000) / 60_000);
    return `${h}h ${m}min`;
  } catch { return "—"; }
}

function humanError(err, fallback = "Ocorreu um erro.") {
  const s = err?.response?.status;
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string" && detail.length < 200) return detail;
  const map = {
    401: "Sessão expirada.", 403: "Sem permissão.",
    404: "Documento não encontrado.", 409: "Conflito.",
    413: "Arquivo grande demais.", 422: "Arquivo inválido.",
    429: "Muitas requisições.", 500: "Erro interno.", 503: "Indisponível.",
  };
  return map[s] || fallback;
}

export default function KnowledgeV2DocDetail({ docId, onClose, onToast, onChange }) {
  const [doc, setDoc] = useState(null);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagesLoading, setPagesLoading] = useState(false);
  const [failedOpen, setFailedOpen] = useState(true);
  const [retrying, setRetrying] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const pollTimerRef = useRef(null);
  const mountedRef = useRef(true);

  // ── Load doc + admin flag ────────────────────────────────────────
  useEffect(() => {
    mountedRef.current = true;
    // Detecta admin via /auth/me para exibir hash SHA-256.
    api.get("/auth/me").then((r) => {
      const role = r.data?.role || "";
      setIsAdmin(role === "super_admin" || role === "owner" || role === "admin");
    }).catch(() => {});
    return () => {
      mountedRef.current = false;
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, []);

  const loadDoc = useCallback(async () => {
    try {
      const { data } = await api.get(`/knowledge/v2/${docId}`);
      if (!mountedRef.current) return;
      setDoc(data);
      return data;
    } catch (err) {
      onToast?.(humanError(err, "Não foi possível carregar o documento."), "error");
      onClose?.();
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [docId, onToast, onClose]);

  const loadPages = useCallback(async () => {
    setPagesLoading(true);
    try {
      const { data } = await api.get(`/knowledge/v2/${docId}/pages`);
      if (!mountedRef.current) return;
      setPages(Array.isArray(data) ? data : []);
    } catch (err) {
      // Não é fatal — o modal ainda mostra o resto.
      if (mountedRef.current) setPages([]);
    } finally {
      if (mountedRef.current) setPagesLoading(false);
    }
  }, [docId]);

  useEffect(() => {
    loadDoc();
    loadPages();
  }, [loadDoc, loadPages]);

  // Polling do doc + pages enquanto não estiver terminal
  useEffect(() => {
    if (!doc) return;
    if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    if (TERMINAL_STATES.has(doc.state)) return;

    const tick = async () => {
      const fresh = await loadDoc();
      if (!fresh) return;
      // Recarrega pages para manter contadores atualizados.
      loadPages();
      if (TERMINAL_STATES.has(fresh.state) && pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
    pollTimerRef.current = setInterval(tick, 2500);
    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [doc?.state, loadDoc, loadPages]);

  // ── Retry seletivo (backend reseta failed→pending automaticamente) ─
  const retryFailed = async () => {
    if (retrying) return;
    setRetrying(true);
    try {
      await api.post(`/knowledge/v2/${docId}/retry`);
      onToast?.("Reprocessamento das páginas com falha iniciado.");
      await loadDoc();
      await loadPages();
      onChange?.();
    } catch (err) {
      onToast?.(humanError(err, "Falha ao reprocessar."), "error");
    } finally {
      setRetrying(false);
    }
  };

  const copyHash = () => {
    if (!doc?.checksum_sha256) return;
    navigator.clipboard.writeText(doc.checksum_sha256).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }).catch(() => {});
  };

  // ── Aggregations ─────────────────────────────────────────────────
  const stats = useMemo(() => {
    const total = doc?.total_pages || pages.length;
    const done = pages.filter((p) => ["extracted", "ocr_ran"].includes(p.status)).length;
    const ocr = pages.filter((p) => p.status === "ocr_ran").length;
    const empty = pages.filter((p) => p.status === "empty").length;
    const failed = pages.filter((p) => p.status === "failed").length;
    return { total, done, ocr, empty, failed };
  }, [doc, pages]);

  const failedPages = useMemo(
    () => pages.filter((p) => p.status === "failed" || p.status === "empty"),
    [pages],
  );

  // Current pipeline step index
  const currentStepIdx = useMemo(() => {
    if (!doc) return -1;
    if (doc.state === "ready" || doc.state === "partially_processed") return PIPELINE.length - 1;
    if (doc.state === "failed") return -1;
    const idx = PIPELINE.findIndex((p) => p.key === doc.state);
    return idx >= 0 ? idx : 0;
  }, [doc?.state]);

  const isProcessing = doc && !TERMINAL_STATES.has(doc.state);

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/40 flex items-end sm:items-center justify-center sm:p-6"
      data-testid="kb2-detail-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="w-full sm:max-w-3xl bg-white sm:rounded-3xl rounded-t-3xl shadow-xl max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="px-5 sm:px-6 py-4 border-b border-slate-100 flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
            <FilePdf size={18} weight="regular" className="text-slate-700" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-[15px] font-semibold text-slate-900 tracking-tight truncate">
              {loading ? "Carregando…" : (doc?.title || "Documento")}
            </h3>
            {doc && (
              <div className="text-[11.5px] text-slate-500 mt-0.5 truncate">
                {doc.original_filename} · {formatBytes(doc.size_bytes)}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="kb2-detail-close"
            className="p-2 rounded-full hover:bg-slate-100 text-slate-500"
          >
            <X size={14} weight="bold" />
          </button>
        </div>

        {loading || !doc ? (
          <div className="flex-1 flex items-center justify-center p-12">
            <CircleNotch size={22} className="animate-spin text-slate-400" />
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto px-5 sm:px-6 py-5 space-y-5">
            {/* Pipeline steps */}
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-2">
                Pipeline
              </div>
              <div className="flex items-center gap-1.5 flex-wrap" data-testid="kb2-detail-pipeline">
                {PIPELINE.map((step, i) => {
                  const state = doc.state === "failed"
                    ? "failed"
                    : i < currentStepIdx ? "done"
                    : i === currentStepIdx ? (TERMINAL_STATES.has(doc.state) ? "done" : "current")
                    : "pending";
                  const cls = state === "done"
                    ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                    : state === "current"
                    ? "bg-slate-900 text-white border-slate-900"
                    : state === "failed"
                    ? "bg-rose-50 text-rose-700 border-rose-200"
                    : "bg-white text-slate-400 border-slate-200";
                  return (
                    <div
                      key={step.key}
                      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] border ${cls}`}
                    >
                      {state === "current" && <CircleNotch size={9} className="animate-spin" />}
                      {state === "done" && <CheckCircle size={10} weight="fill" />}
                      {step.label}
                    </div>
                  );
                })}
              </div>
              {doc.error && (
                <div className="mt-3 flex items-start gap-2 text-[12px] text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
                  <WarningCircle size={13} weight="fill" className="shrink-0 mt-[1px]" />
                  <span className="break-words">{doc.error}</span>
                </div>
              )}
            </div>

            {/* Progress big */}
            {(stats.total > 0 || isProcessing) && (
              <div className="rounded-2xl border border-slate-200 bg-slate-50/50 p-4" data-testid="kb2-detail-progress">
                <div className="flex items-baseline justify-between mb-2">
                  <div className="text-[12px] text-slate-600">Progresso</div>
                  <div className="text-[13px] font-semibold tabular-nums text-slate-900">
                    {stats.done} de {stats.total || "?"} páginas
                    {stats.total > 0 && ` · ${((stats.done / stats.total) * 100).toFixed(1)}%`}
                  </div>
                </div>
                {stats.total > 0 && (
                  <div className="h-2 rounded-full bg-slate-200 overflow-hidden flex">
                    <div
                      className="h-full bg-slate-900 transition-all"
                      style={{ width: `${(stats.done / stats.total) * 100}%` }}
                    />
                    {stats.failed > 0 && (
                      <div
                        className="h-full bg-rose-400 transition-all"
                        style={{ width: `${(stats.failed / stats.total) * 100}%` }}
                      />
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Metrics grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <MetricCard label="Total de páginas" value={stats.total || 0} />
              <MetricCard label="Concluídas" value={stats.done} tone="emerald" />
              <MetricCard label="Via OCR" value={stats.ocr} tone="indigo" />
              <MetricCard label="Vazias" value={stats.empty} tone="slate" />
              <MetricCard label="Com erro" value={stats.failed} tone={stats.failed > 0 ? "rose" : "slate"} />
              <MetricCard label="Blocos indexados" value={doc.chunks_count || 0} />
              <MetricCard label="Versão" value={`v${doc.document_version || 1}`} />
              <MetricCard label="Duração" value={durationText(doc.uploaded_at, doc.processed_at)} />
            </div>

            {/* Failed pages panel */}
            {failedPages.length > 0 && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50/40 overflow-hidden" data-testid="kb2-failed-pages">
                <button
                  type="button"
                  onClick={() => setFailedOpen((v) => !v)}
                  className="w-full px-4 py-3 flex items-center gap-2 text-left"
                >
                  {failedOpen ? <CaretDown size={12} /> : <CaretRight size={12} />}
                  <WarningCircle size={14} weight="fill" className="text-rose-600" />
                  <span className="text-[13px] font-semibold text-slate-900">
                    Páginas com problema
                  </span>
                  <span className="text-[11px] text-slate-500">
                    · {failedPages.length}
                  </span>
                  <span className="ml-auto">
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); retryFailed(); }}
                      disabled={retrying}
                      data-testid="kb2-retry-failed"
                      className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-[11px] font-medium border border-slate-900 bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50"
                    >
                      {retrying ? <CircleNotch size={11} className="animate-spin" /> : <ArrowClockwise size={11} weight="bold" />}
                      Reprocessar
                    </button>
                  </span>
                </button>
                {failedOpen && (
                  <div className="max-h-56 overflow-y-auto divide-y divide-rose-100 border-t border-rose-200">
                    {failedPages.map((p) => (
                      <div
                        key={`${p.page_number}-${p.status}`}
                        className="px-4 py-2.5 text-[12px] flex items-start gap-3"
                        data-testid={`kb2-failed-page-${p.page_number}`}
                      >
                        <div className="shrink-0 h-6 w-9 rounded bg-white border border-rose-200 flex items-center justify-center text-[11px] font-medium text-rose-700 tabular-nums">
                          {p.page_number}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-slate-800 font-medium">
                            {PAGE_STATUS_LABEL[p.status] || p.status}
                            {p.extraction_method && (
                              <span className="ml-2 text-slate-500 font-normal">
                                · {p.extraction_method}
                              </span>
                            )}
                          </div>
                          {p.error && (
                            <div className="text-[11.5px] text-rose-700 mt-0.5 break-words">
                              {p.error}
                            </div>
                          )}
                          {p.status === "empty" && !p.error && (
                            <div className="text-[11.5px] text-slate-500 mt-0.5">
                              Nenhum texto detectado (nem extração nem OCR).
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Meta */}
            <div className="rounded-2xl border border-slate-200 p-4">
              <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-2">
                Metadados
              </div>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-6 text-[12px]">
                <MetaRow label="Enviado em" value={formatDateTime(doc.uploaded_at)} />
                <MetaRow label="Concluído em" value={formatDateTime(doc.processed_at)} />
                <MetaRow label="Estado atual" value={STATE_LABEL[doc.state] || doc.state} />
                <MetaRow label="OCR utilizado" value={doc.ocr_used ? "Sim" : "Não"} />
                <MetaRow label="Tamanho" value={formatBytes(doc.size_bytes)} />
                <MetaRow label="Tipo" value={doc.mime_type || "application/pdf"} />
                {isAdmin && doc.checksum_sha256 && (
                  <div className="col-span-1 sm:col-span-2">
                    <dt className="text-slate-500 text-[11px] uppercase tracking-wider">
                      Hash (suporte / admin)
                    </dt>
                    <dd className="flex items-center gap-2 mt-0.5">
                      <code className="text-[10.5px] text-slate-700 bg-slate-100 rounded px-2 py-1 truncate flex-1 font-mono">
                        {doc.checksum_sha256}
                      </code>
                      <button
                        type="button"
                        onClick={copyHash}
                        className="p-1.5 rounded-full hover:bg-slate-100 text-slate-500"
                        data-testid="kb2-copy-hash"
                        title="Copiar hash"
                      >
                        {copied ? <Check size={12} weight="bold" /> : <Copy size={12} />}
                      </button>
                    </dd>
                  </div>
                )}
              </dl>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="px-5 sm:px-6 py-3 border-t border-slate-100 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            data-testid="kb2-detail-done"
            className="px-4 py-2 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-[13px]"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}

function MetricCard({ label, value, tone = "slate" }) {
  const toneCls = {
    slate:   "text-slate-800",
    emerald: "text-emerald-700",
    rose:    "text-rose-700",
    indigo:  "text-indigo-700",
  }[tone] || "text-slate-800";
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-3 py-2.5">
      <div className="text-[10.5px] uppercase tracking-wider text-slate-400 font-medium">
        {label}
      </div>
      <div className={`text-[16px] font-semibold tabular-nums mt-0.5 ${toneCls}`}>
        {value}
      </div>
    </div>
  );
}

function MetaRow({ label, value }) {
  return (
    <div>
      <dt className="text-slate-500 text-[11px] uppercase tracking-wider">{label}</dt>
      <dd className="text-slate-800 mt-0.5">{value}</dd>
    </div>
  );
}
